import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { getVideoTutorialInicioQuizThunk } from "../../../../store/quiz/quiz.thunks";
import { Icon } from "@iconify/react";
import { useNavigate } from "react-router-dom";
import { marcarTutorialVisto } from "../../../../utils/tutorialWatched";
import "./tutorial.css";

const TIPO_INICIO_QUIZ = "inicio_quiz";

/* ─── helpers ─── */
const getYoutubeId = (url) => {
  if (!url) return null;
  const patterns = [
    /youtube\.com\/watch\?v=([^&]+)/,
    /youtu\.be\/([^?]+)/,
    /youtube\.com\/embed\/([^?]+)/,
  ];
  for (const p of patterns) {
    const m = url.match(p);
    if (m) return m[1];
  }
  return null;
};

const pickUrl = (block, fallback) =>
  block?.video_url_generada || block?.video_url_externa || block?.video_url || fallback || null;

const KIND_META = {
  tutorial: { label: "Bienvenida", icon: "solar:play-circle-bold" },
  instrucciones: { label: "Instrucciones", icon: "solar:book-2-bold" },
};

/* ─── componente ─── */
const TutorialVideoPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { videoTutorial } = useSelector((state) => state.quiz);
  const userId = useSelector(
    (s) => s.auth?.user?.user_id || s.auth?.user?.id,
  );

  const videoRef = useRef(null);
  const hideControlsTimeout = useRef(null);

  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [activeKind, setActiveKind] = useState("tutorial");
  const [videoError, setVideoError] = useState(false);

  useEffect(() => {
    dispatch(getVideoTutorialInicioQuizThunk());
  }, [dispatch]);

  const videos = useMemo(() => {
    if (!videoTutorial) return { tutorial: null, instrucciones: null };
    return {
      tutorial: pickUrl(
        videoTutorial.tutorial,
        videoTutorial.video_tutorial_url_generada || videoTutorial.video_url,
      ),
      instrucciones: pickUrl(videoTutorial.instrucciones),
    };
  }, [videoTutorial]);

  // Ajustar activeKind si el actual no tiene video pero el otro sí
  useEffect(() => {
    if (!videos[activeKind] && videos.tutorial) setActiveKind("tutorial");
    else if (!videos[activeKind] && videos.instrucciones) setActiveKind("instrucciones");
  }, [videos, activeKind]);

  const availableKinds = Object.keys(videos).filter((k) => videos[k]);
  const rawUrl = videos[activeKind];
  const youtubeId = getYoutubeId(rawUrl);
  const isYT = !!youtubeId;
  const embedUrl = youtubeId
    ? `https://www.youtube.com/embed/${youtubeId}?autoplay=1&rel=0&modestbranding=1&showinfo=0`
    : null;

  /* fullscreen */
  useEffect(() => {
    const enter = async () => {
      try {
        if (!document.fullscreenElement)
          await document.documentElement.requestFullscreen();
      } catch {}
    };
    enter();
    return () => {
      if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
    };
  }, []);

  /* autoplay para video nativo — reacciona a cambios de rawUrl.
   *
   * Notas:
   * - Se espera al evento `canplay` porque llamar `play()` antes de que el
   *   source esté cargado tira `NotSupportedError` (típico al cambiar src).
   * - iOS Safari solo permite autoplay si `muted=true`. Arrancamos muteados
   *   y el usuario decide unmute con los controles.
   * - Si `play()` es rechazado (política del navegador o formato no soportado)
   *   dejamos `isPlaying=false` para que se muestre el botón central de play.
   */
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !rawUrl || isYT) return;

    let cancelled = false;
    setVideoError(false);

    // Forzamos recarga del source cuando cambia rawUrl.
    try {
      video.load();
    } catch {
      /* ignorar */
    }

    const tryPlay = () => {
      if (cancelled || !video) return;
      // iOS requiere muted para autoplay; también aumenta el chance de éxito en desktop.
      video.muted = true;
      setIsMuted(true);
      const p = video.play();
      if (p && typeof p.catch === "function") {
        p.then(() => {
          if (!cancelled) setIsPlaying(true);
        }).catch((err) => {
          if (cancelled) return;
          setIsPlaying(false);
          // NotSupportedError = codec/URL inválida. NotAllowedError = política
          // de autoplay (el usuario deberá presionar play; no es un error real).
          if (err && err.name === "NotSupportedError") setVideoError(true);
        });
      }
    };

    // Esperar a que el source esté listo para reproducir.
    if (video.readyState >= 3 /* HAVE_FUTURE_DATA */) {
      tryPlay();
    } else {
      video.addEventListener("canplay", tryPlay, { once: true });
    }

    return () => {
      cancelled = true;
      video.removeEventListener("canplay", tryPlay);
    };
  }, [rawUrl, isYT]);

  /* eventos de progreso para video nativo */
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const update = () => {
      if (!video.duration) return;
      setCurrentTime(video.currentTime);
      setDuration(video.duration);
      setProgress((video.currentTime / video.duration) * 100);
    };

    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    const onEnded = () => {
      setIsPlaying(false);
      marcarTutorialVisto(userId, TIPO_INICIO_QUIZ, activeKind);
    };

    video.addEventListener("timeupdate", update);
    video.addEventListener("loadedmetadata", update);
    video.addEventListener("play", onPlay);
    video.addEventListener("pause", onPause);
    video.addEventListener("ended", onEnded);

    return () => {
      video.removeEventListener("timeupdate", update);
      video.removeEventListener("loadedmetadata", update);
      video.removeEventListener("play", onPlay);
      video.removeEventListener("pause", onPause);
      video.removeEventListener("ended", onEnded);
    };
  }, [rawUrl, userId, activeKind]);

  const handleMouseMove = useCallback(() => {
    setShowControls(true);
    if (hideControlsTimeout.current) clearTimeout(hideControlsTimeout.current);
    hideControlsTimeout.current = setTimeout(
      () => setShowControls(false),
      3000,
    );
  }, []);

  const togglePlay = (e) => {
    e?.stopPropagation();
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      const p = video.play();
      if (p && typeof p.catch === "function") {
        p.catch(() => {
          // NotSupportedError / NotAllowedError / etc. — no bloqueamos.
          setVideoError(true);
          setIsPlaying(false);
        });
      }
    } else {
      video.pause();
    }
  };

  const toggleMute = (e) => {
    e?.stopPropagation();
    const video = videoRef.current;
    if (!video) return;
    video.muted = !video.muted;
    setIsMuted(video.muted);
  };

  const handleVolume = (e) => {
    e.stopPropagation();
    const val = Number(e.target.value);
    const video = videoRef.current;
    if (!video) return;
    video.volume = val;
    video.muted = val === 0;
    setVolume(val);
    setIsMuted(val === 0);
  };

  const handleProgressClick = (e) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    const video = videoRef.current;
    if (video?.duration) video.currentTime = pct * video.duration;
  };

  const handleClose = (e) => {
    e?.stopPropagation();
    navigate("/");
  };

  const fmt = (s) => {
    if (!s || isNaN(s)) return "0:00";
    return `${Math.floor(s / 60)}:${Math.floor(s % 60)
      .toString()
      .padStart(2, "0")}`;
  };

  if (!rawUrl) return null;

  return (
    <div
      className="tutorial-container"
      onMouseMove={handleMouseMove}
      onClick={isYT ? undefined : togglePlay}
    >
      {/* ══ YOUTUBE ══ */}
      {isYT ? (
        <div className="tutorial-yt-wrapper">
          <iframe
            key={activeKind}
            className="tutorial-yt-iframe"
            src={embedUrl}
            title="Tutorial"
            allow="autoplay; fullscreen; picture-in-picture"
            allowFullScreen
            frameBorder="0"
          />
        </div>
      ) : (
        /* ══ VIDEO NATIVO ══ */
        <>
          <video
            key={activeKind}
            ref={videoRef}
            src={rawUrl}
            className="tutorial-video"
            playsInline
            webkit-playsinline="true"
            preload="metadata"
            onError={() => {
              setIsPlaying(false);
              setVideoError(true);
            }}
            onLoadedData={() => setVideoError(false)}
          />

          <div className="tutorial-overlay" />

          {videoError && (
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                position: "absolute",
                inset: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                background: "rgba(0,0,0,0.75)",
                color: "white",
                zIndex: 20,
                padding: "1.5rem",
                textAlign: "center",
              }}
            >
              <Icon
                icon="mdi:alert-circle-outline"
                style={{ fontSize: 48, color: "#f59e0b", marginBottom: 12 }}
              />
              <h5 style={{ color: "white", marginBottom: 6 }}>
                No se pudo cargar el video
              </h5>
              <p
                style={{
                  color: "rgba(255,255,255,0.7)",
                  fontSize: "0.9rem",
                  maxWidth: 380,
                  marginBottom: 16,
                }}
              >
                El navegador no puede reproducir este archivo. Puede ser un
                formato no soportado o un problema de conexión. Puedes marcarlo
                como visto para continuar.
              </p>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  marcarTutorialVisto(userId, TIPO_INICIO_QUIZ, activeKind);
                  handleClose();
                }}
                style={{
                  padding: "0.6rem 1.4rem",
                  background: "#10b981",
                  color: "white",
                  border: "none",
                  borderRadius: 999,
                  fontWeight: 700,
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <Icon icon="mdi:check-circle" style={{ fontSize: 18 }} />
                Marcar como visto y continuar
              </button>
            </div>
          )}

          {!isPlaying && !videoError && (
            <button className="center-play" onClick={togglePlay}>
              <div className="center-play-ring" />
              <Icon icon="solar:play-bold" />
            </button>
          )}

          <div className={`bottom-controls ${showControls ? "show" : "hide"}`}>
            <div className="progress-bar" onClick={handleProgressClick}>
              <div
                className="progress-fill"
                style={{ width: `${progress}%` }}
              />
              <div
                className="progress-thumb"
                style={{ left: `${progress}%` }}
              />
            </div>

            <div className="control-row">
              <div className="controls-left">
                <button onClick={togglePlay} className="ctrl-btn">
                  <Icon
                    icon={isPlaying ? "solar:pause-bold" : "solar:play-bold"}
                  />
                </button>

                <div
                  className="volume-group"
                  onClick={(e) => e.stopPropagation()}
                >
                  <button className="ctrl-btn" onClick={toggleMute}>
                    <Icon
                      icon={
                        isMuted || volume === 0
                          ? "solar:volume-cross-bold"
                          : volume < 0.5
                            ? "solar:volume-small-bold"
                            : "solar:volume-loud-bold"
                      }
                    />
                  </button>
                  <input
                    type="range"
                    className="volume-slider"
                    min="0"
                    max="1"
                    step="0.01"
                    value={isMuted ? 0 : volume}
                    onChange={handleVolume}
                  />
                </div>

                <span className="time-display">
                  {fmt(currentTime)} / {fmt(duration)}
                </span>
              </div>

              <div className="controls-right">
                <span className="tutorial-label">
                  <Icon icon={KIND_META[activeKind].icon} />
                  {KIND_META[activeKind].label}
                </span>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Tabs (siempre visibles si hay más de un video) */}
      {availableKinds.length > 1 && (
        <div
          className="tutorial-tabs show"
          onClick={(e) => e.stopPropagation()}
          style={{
            position: "absolute",
            top: 24,
            left: "50%",
            transform: "translateX(-50%)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 8,
            zIndex: 25,
            pointerEvents: "auto",
          }}
        >
          <span
            style={{
              fontSize: "0.72rem",
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: "rgba(255,255,255,0.85)",
              fontWeight: 700,
              textShadow: "0 2px 8px rgba(0,0,0,0.7)",
            }}
          >
            Elige qué video ver
          </span>
          <div
            style={{
              display: "flex",
              gap: 10,
              padding: 8,
              background: "rgba(15, 20, 45, 0.85)",
              backdropFilter: "blur(14px)",
              borderRadius: 999,
              border: "1.5px solid rgba(119,73,248,0.55)",
              boxShadow:
                "0 10px 32px rgba(0,0,0,0.55), 0 0 0 4px rgba(119,73,248,0.15)",
            }}
          >
            {availableKinds.map((k) => {
              const active = activeKind === k;
              return (
                <button
                  key={k}
                  onClick={() => setActiveKind(k)}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "0.65rem 1.5rem",
                    fontSize: "0.98rem",
                    border: "none",
                    borderRadius: 999,
                    background: active
                      ? "linear-gradient(135deg, #7749F8, #5b34c1)"
                      : "transparent",
                    color: active ? "white" : "rgba(255,255,255,0.85)",
                    cursor: "pointer",
                    fontWeight: 700,
                    boxShadow: active
                      ? "0 6px 20px rgba(119,73,248,0.5)"
                      : "none",
                    transform: active ? "scale(1.02)" : "scale(1)",
                    transition:
                      "background 0.2s, transform 0.15s, box-shadow 0.2s",
                  }}
                >
                  <Icon icon={KIND_META[k].icon} style={{ fontSize: 20 }} />
                  {KIND_META[k].label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Badge y close */}
      <div className={`tutorial-badge ${showControls ? "show" : "hide"}`}>
        <Icon icon="solar:play-circle-bold" />
        Tutorial
      </div>

      {/* Botón "Ya lo vi" — necesario para YouTube y como respaldo manual */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          marcarTutorialVisto(userId, TIPO_INICIO_QUIZ, activeKind);
          handleClose();
        }}
        className={`${showControls ? "show" : "hide"}`}
        style={{
          position: "absolute",
          bottom: 24,
          right: 24,
          zIndex: 30,
          padding: "0.65rem 1.4rem",
          background: "rgba(16,185,129,0.9)",
          color: "white",
          border: "none",
          borderRadius: 999,
          fontSize: "0.9rem",
          fontWeight: 700,
          cursor: "pointer",
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          boxShadow: "0 6px 20px rgba(16,185,129,0.4)",
          transition: "opacity 0.2s",
        }}
        title="Marca el video como visto y regresa"
      >
        <Icon icon="mdi:check-circle" style={{ fontSize: 18 }} />
        Ya lo vi
      </button>

      <button
        className={`close-btn ${showControls ? "show" : "hide"}`}
        onClick={handleClose}
      >
        <Icon icon="solar:close-circle-bold" />
      </button>
    </div>
  );
};

export default TutorialVideoPage;
